import { useCallback, useEffect, useState } from "react";
import { passkeyApi } from "../api";
import { useBiometricSetup } from "../hooks/use-biometric-setup";
import { useGlobalNotifications } from "../hooks/use-global-notifications";
import { isNative } from "../platform";
import { BiometricsStorage } from "../helpers/biometrics-storage";

export interface PasskeyDevice {
  id: string;
  credential_id: string;
  created_at: string;
  updated_at: string;
  transports?: string[];
  friendly_name?: string | null;
  last_used_at?: string | null;
  auth_type?: string | null;
}

type UsePasskeysArgs = {
  initialPasskeys?: PasskeyDevice[];
};

export const usePasskeys = ({ initialPasskeys = [] }: UsePasskeysArgs = {}) => {
  const { showError, showSuccess } = useGlobalNotifications();
  const [devices, setDevices] = useState<PasskeyDevice[]>(initialPasskeys);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    handleSetup: setupBiometric,
    isLoading: setupLoading,
    error: setupError,
  } = useBiometricSetup();

  // Always sync with initialPasskeys (prefetched data from context)
  useEffect(() => {
    setDevices(initialPasskeys);
  }, [initialPasskeys]);

  // Load user's passkeys (WebAuthn from backend + native biometric if enabled)
  const loadPasskeys = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Always fetch WebAuthn credentials from backend
      let webauthnDevices: PasskeyDevice[] = [];
      try {
        webauthnDevices = await passkeyApi.listCredentials();
      } catch {
        // Non-critical on native — WebAuthn may not be set up
        if (!isNative()) throw new Error("Failed to load passkeys");
      }

      // On native: also include local biometric credential if enabled
      if (isNative() && BiometricsStorage.isBiometricsEnabled()) {
        const biometryType = BiometricsStorage.getBiometricsType?.() ?? 'unknown';
        const biometricLabel = biometryType === 'face' ? 'Face ID' : biometryType === 'fingerprint' ? 'Touch ID' : 'Biometric';
        const nativeDevice: PasskeyDevice = {
          id: 'native-biometric',
          credential_id: 'native-biometric',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          friendly_name: `This device (${biometricLabel})`,
          auth_type: 'native',
        };
        setDevices([nativeDevice, ...webauthnDevices]);
      } else {
        setDevices(webauthnDevices);
      }
    } catch (err: any) {
      const errorMsg = err?.message || "Failed to load passkeys";
      setError(errorMsg);
      console.error("Failed to load passkeys:", err);
      showError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, []);

  // Add a new passkey
  const addPasskey = useCallback(async () => {
    try {
      setError(null);
      const success = await setupBiometric();
      if (success) {
        // Reload passkeys after successful registration
        await loadPasskeys();
        showSuccess("Biometric credential added");
      }
      return success;
    } catch (err: any) {
      const errorMsg = err?.message || "Failed to add passkey";
      setError(errorMsg);
      showError(errorMsg);
      return false;
    }
  }, [setupBiometric, loadPasskeys, showError, showSuccess]);

  // Remove a passkey
  const removePasskey = useCallback(async (credentialId: string) => {
    try {
      setError(null);

      // Native: clear local biometric credentials
      if (isNative() && credentialId === 'native-biometric') {
        const { clearNativeCredentials } = await import('../helpers/native-biometric');
        await clearNativeCredentials();
        BiometricsStorage.setBiometricsEnabled(false);
        BiometricsStorage.setBiometricsType(null);
        setDevices([]);
        showSuccess("Biometric credential removed");
        return;
      }

      await passkeyApi.deleteCredential(credentialId);
      setDevices((prev) => prev.filter((device) => device.id !== credentialId));
      showSuccess("Biometric credential removed");
    } catch (err: any) {
      const errorMsg = err?.message || "Failed to remove passkey";
      setError(errorMsg);
      showError(errorMsg);
      await loadPasskeys();
      throw err;
    }
  }, [loadPasskeys, showError, showSuccess]);

  // Rename a passkey
  const renamePasskey = useCallback(async (credentialId: string, friendlyName: string) => {
    try {
      setError(null);
      await passkeyApi.renameCredential(credentialId, friendlyName);
      setDevices((prev) =>
        prev.map((d) => (d.id === credentialId ? { ...d, friendly_name: friendlyName } : d))
      );
      showSuccess("Credential renamed");
    } catch (err: any) {
      const errorMsg = err?.message || "Failed to rename passkey";
      setError(errorMsg);
      showError(errorMsg);
    }
  }, [showError, showSuccess]);

  return {
    devices,
    loading: loading || setupLoading,
    error: error || setupError,
    addPasskey,
    removePasskey,
    renamePasskey,
    refreshPasskeys: loadPasskeys,
  };
};
